import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { updatePayoutDestination } from '../firebase/firestore';
import { requestTelegramLinkViaBot } from '../firebase/auth';
import { useI18n } from '../i18n/I18nContext';
import { useProMessages } from '../i18n/pro';
import type { LocaleCode } from '../i18n/types';
import { getTelegramInitData, isTelegramMiniApp } from '../telegram/webApp';
import './Settings.css';

const Settings: React.FC = () => {
  const { user, firebaseUser, linkTelegram, refreshUser } = useAuth();
  const inMiniApp = isTelegramMiniApp();
  const [botLinkUrl, setBotLinkUrl] = useState<string | null>(null);
  const [waitingForBot, setWaitingForBot] = useState(false);
  const pollTimerRef = useRef<number | null>(null);
  const pollDeadlineRef = useRef<number>(0);
  const { t, locale, setLocale, locales } = useI18n();
  const proLabels = useProMessages().app;
  const isPro = !!user?.proUntil && user.proUntil.getTime() > Date.now();
  const [tonAddress, setTonAddress] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [linkingTelegram, setLinkingTelegram] = useState(false);
  const [languageSuccess, setLanguageSuccess] = useState(false);

  useEffect(() => {
    if (user?.agent) {
      setTonAddress(user.agent.tonAddress || '');
      setDisplayName(user.agent.displayName || '');
    }
  }, [user]);

  const handleLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const next = event.target.value as LocaleCode;
    setLocale(next);
    setLanguageSuccess(true);
    setTimeout(() => setLanguageSuccess(false), 2000);
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setWaitingForBot(false);
  };

  useEffect(() => stopPolling, []);

  // Dès que le bot a lié le compte (telegramUserId présent), on arrête d'attendre
  useEffect(() => {
    if (user?.telegramUserId && waitingForBot) {
      stopPolling();
      setBotLinkUrl(null);
      setSuccess(t.settings.telegramLinked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.telegramUserId]);

  /**
   * Hors Mini App : génère un code à usage unique, ouvre le bot avec
   * `/start link_<code>` et attend que le bot confirme la liaison.
   */
  const handleLinkViaBot = async () => {
    setError(null);
    setSuccess(null);
    setLinkingTelegram(true);
    try {
      const { url } = await requestTelegramLinkViaBot();
      setBotLinkUrl(url);
      window.open(url, '_blank', 'noopener');

      stopPolling();
      setWaitingForBot(true);
      pollDeadlineRef.current = Date.now() + 10 * 60 * 1000;
      pollTimerRef.current = window.setInterval(() => {
        if (Date.now() > pollDeadlineRef.current) {
          stopPolling();
          return;
        }
        refreshUser().catch(() => undefined);
      }, 3000);
    } catch (err: any) {
      setError(err.message || t.settings.linkTelegramError);
    } finally {
      setLinkingTelegram(false);
    }
  };

  const handleLinkTelegram = async () => {
    if (!inMiniApp) {
      await handleLinkViaBot();
      return;
    }

    const initData = getTelegramInitData();
    if (!initData || !window.Telegram?.WebApp?.initDataUnsafe?.user) {
      setError(t.settings.telegramUserDataNotAvailable);
      return;
    }

    setError(null);
    setSuccess(null);
    setLinkingTelegram(true);

    try {
      await linkTelegram(initData);
      setSuccess(t.settings.telegramLinked);
      setTimeout(() => window.location.reload(), 1000);
    } catch (err: any) {
      setError(err.message || t.settings.linkTelegramError);
    } finally {
      setLinkingTelegram(false);
    }
  };

  const handleTonAddressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firebaseUser) {
      setError(t.settings.mustBeLoggedIn);
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (!firebaseUser?.uid) throw new Error('User ID not available');
      await updatePayoutDestination(firebaseUser.uid, tonAddress);
      setSuccess(t.settings.tonUpdated);
      setTimeout(() => window.location.reload(), 1000);
    } catch (err: any) {
      setError(err.message || t.settings.updateError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings">
      <div className="settings-header">
        <p className="settings-eyebrow">{t.settings.eyebrow}</p>
        <h1>{t.settings.title}</h1>
      </div>

      <div className="settings-section">
        <h2>{t.settings.languageSection}</h2>
        <div className="setting-card setting-card-rows">
          <div className="setting-row">
            <label htmlFor="defaultLanguage">{t.settings.defaultLanguage}</label>
            <select
              id="defaultLanguage"
              className="setting-language-select"
              value={locale}
              onChange={handleLanguageChange}
            >
              {locales.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.nativeLabel} ({item.code})
                </option>
              ))}
            </select>
          </div>
          <p className="setting-hint setting-hint-inline">{t.settings.languageHint}</p>
          {languageSuccess && (
            <div className="success-message">{t.settings.languageSaved}</div>
          )}
        </div>
      </div>

      <div className="settings-section">
        <h2>{t.settings.profile}</h2>
        <div className="setting-card">
          <div className="setting-item">
            <label>{t.settings.email}</label>
            <p className="setting-value">{firebaseUser?.email || t.app.common.notSet}</p>
          </div>
          <div className="setting-item">
            <label>{t.settings.telegramId}</label>
            {user?.telegramUserId ? (
              <p className="setting-value">{user.telegramUserId}</p>
            ) : (
              <div>
                <p className="setting-value" style={{ color: '#666', fontStyle: 'italic' }}>
                  {t.settings.notLinked}
                </p>
                <button
                  onClick={handleLinkTelegram}
                  disabled={linkingTelegram || waitingForBot}
                  className="link-telegram-btn"
                >
                  {linkingTelegram || waitingForBot ? t.settings.linking : t.settings.linkTelegram}
                </button>
                {waitingForBot && botLinkUrl && (
                  <p className="setting-hint">
                    ⏳{' '}
                    <a href={botLinkUrl} target="_blank" rel="noopener noreferrer">
                      {t.app.layout.openBot} → /start
                    </a>
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="setting-item">
            <label>{t.settings.displayName}</label>
            <p className="setting-value">{displayName || t.app.common.notSet}</p>
            <p className="setting-hint">
              {t.settings.displayNameHint}
            </p>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h2>{t.settings.withdrawal}</h2>
        <div className="setting-card">
          <form onSubmit={handleTonAddressSubmit} className="setting-form">
            <div className="form-group">
              <label htmlFor="tonAddress">{t.settings.tonAddress}</label>
              <input
                id="tonAddress"
                type="text"
                value={tonAddress}
                onChange={(e) => setTonAddress(e.target.value)}
                className="form-control"
                placeholder={t.settings.tonAddressPlaceholder}
                required
              />
              <p className="form-hint">
                {t.settings.tonAddressHint}
              </p>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <button type="submit" disabled={loading || !firebaseUser} className="save-btn">
              {loading ? t.app.common.saving : t.app.common.save}
            </button>
          </form>
        </div>
      </div>

      <div className="settings-section">
        <h2>{t.settings.about}</h2>
        <div className="setting-card">
          <p>
            <strong>{t.settings.commission}</strong> {isPro ? proLabels.settingsCommissionPro : t.settings.commissionValue}
          </p>
          <p>
            <strong>{t.settings.minWithdrawal}</strong> {t.settings.minWithdrawalValue}
          </p>
          <p>
            <strong>{t.settings.starsCurrency}</strong> {t.settings.starsCurrencyValue}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Settings;
